import { collection, doc, setDoc, addDoc, getDocs, deleteDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { encrypt } from './webCryptoService';
import { decryptWithMasterPassword } from './masterPasswordCrypto';

/**
 * 데이터 가져오기 서비스
 * JSON 파일 가져오기 및 마스터 패스워드 복호화 지원
 */
export const importService = {
  /**
   * 가져오기 데이터 복호화
   * @param {Object} encryptedData - 암호화된 데이터
   * @param {string} masterPassword - 마스터 패스워드
   * @returns {Promise<Object>} 복호화된 데이터
   */
  async decryptImportData(encryptedData, masterPassword) {
    try {
      if (!encryptedData.encrypted || !encryptedData.data) {
        throw new Error('암호화된 데이터가 아닙니다.');
      }

      const decryptedJson = await decryptWithMasterPassword(encryptedData, masterPassword);
      const decryptedData = JSON.parse(decryptedJson);
      
      // 데이터 구조 검증
      if (!decryptedData.metadata || !decryptedData.data) {
        throw new Error('올바른 Password Wallet 데이터가 아닙니다.');
      }

      return decryptedData;
    } catch (error) {
      console.error('Import data decryption failed:', error);
      throw new Error('복호화에 실패했습니다. 패스워드를 확인해주세요.');
    }
  },

  /**
   * 사용자 데이터 가져오기
   * @param {string} userId - 사용자 UID
   * @param {Object} importConfig - 가져오기 설정
   * @returns {Promise<Object>} 가져오기 결과
   */
  async importUserData(userId, importConfig) {
    const { data, options, preview } = importConfig;
    const results = {
      success: true,
      imported: {
        passwords: 0,
        creditCards: 0,
        apiKeys: 0,
        secureNotes: 0,
        attachments: 0
      },
      skipped: {
        passwords: 0,
        creditCards: 0,
        apiKeys: 0,
        secureNotes: 0,
        attachments: 0
      },
      errors: []
    };

    try {
      // 대체 모드일 경우 기존 데이터 삭제
      if (options.mergeMode === 'replace') {
        await this.clearUserData(userId);
      }

      const batch = writeBatch(db);
      let batchCount = 0;

      // 각 데이터 타입별로 가져오기
      if (data.data.passwords && Array.isArray(data.data.passwords)) {
        const passwordResult = await this.importPasswords(
          userId, 
          data.data.passwords, 
          options, 
          batch,
          batchCount
        );
        results.imported.passwords = passwordResult.imported;
        results.skipped.passwords = passwordResult.skipped;
        results.errors.push(...passwordResult.errors);
        batchCount += passwordResult.imported;
      }

      if (data.data.creditCards && Array.isArray(data.data.creditCards)) {
        const cardResult = await this.importCreditCards(
          userId, 
          data.data.creditCards, 
          options, 
          batch,
          batchCount
        );
        results.imported.creditCards = cardResult.imported;
        results.skipped.creditCards = cardResult.skipped;
        results.errors.push(...cardResult.errors);
        batchCount += cardResult.imported;
      }

      if (data.data.apiKeys && Array.isArray(data.data.apiKeys)) {
        const apiKeyResult = await this.importApiKeys(
          userId, 
          data.data.apiKeys, 
          options, 
          batch,
          batchCount
        );
        results.imported.apiKeys = apiKeyResult.imported;
        results.skipped.apiKeys = apiKeyResult.skipped;
        results.errors.push(...apiKeyResult.errors);
        batchCount += apiKeyResult.imported;
      }

      if (data.data.secureNotes && Array.isArray(data.data.secureNotes)) {
        const notesResult = await this.importSecureNotes(
          userId, 
          data.data.secureNotes, 
          options
        );
        results.imported.secureNotes = notesResult.imported;
        results.imported.attachments = notesResult.attachments;
        results.skipped.secureNotes = notesResult.skipped;
        results.errors.push(...notesResult.errors);
      }

      // 배치 커밋 (보안 메모는 별도 처리)
      if (batchCount > 0) {
        await batch.commit();
      }

    } catch (error) {
      console.error('Import failed:', error);
      results.success = false;
      results.errors.push('가져오기 중 오류가 발생했습니다.');
      throw error;
    }

    return results;
  },

  /**
   * 패스워드 데이터 가져오기
   */
  async importPasswords(userId, passwords, options, batch, batchCount) {
    const result = { imported: 0, skipped: 0, errors: [] };
    
    try {
      // 기존 패스워드 목록 (중복 검사용)
      let existingPasswords = [];
      if (options.skipDuplicates) {
        const passwordsRef = collection(db, 'users', userId, 'passwords');
        const snapshot = await getDocs(passwordsRef);
        existingPasswords = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      for (const passwordData of passwords) {
        try {
          // 중복 검사
          if (options.skipDuplicates && this.isDuplicatePassword(passwordData, existingPasswords)) {
            result.skipped++;
            continue;
          }

          // 배치 크기 제한 (Firestore 배치는 최대 500개)
          if (batchCount >= 450) {
            await batch.commit();
            batchCount = 0;
          }

          // 데이터 암호화
          const encryptedData = {
            site: passwordData.site || '',
            username: passwordData.username ? await encrypt(passwordData.username, userId) : null,
            email: passwordData.email ? await encrypt(passwordData.email, userId) : null,
            password: passwordData.password && passwordData.password !== '****' ? 
              await encrypt(passwordData.password, userId) : null,
            url: passwordData.url || '',
            category: passwordData.category || 'other',
            notes: passwordData.notes ? await encrypt(passwordData.notes, userId) : null,
            strength: passwordData.strength || null,
            tags: passwordData.tags || [],
            createdAt: options.preserveTimestamps && passwordData.createdAt ? 
              passwordData.createdAt : serverTimestamp(),
            updatedAt: options.preserveTimestamps && passwordData.updatedAt ? 
              passwordData.updatedAt : serverTimestamp(),
            lastUsed: passwordData.lastUsed || null
          };

          const docRef = doc(collection(db, 'users', userId, 'passwords'));
          batch.set(docRef, encryptedData);
          result.imported++;
          batchCount++;
        } catch (error) {
          console.error('Password import error:', error);
          result.errors.push(`패스워드 "${passwordData.site}" 가져오기 실패`);
        }
      }
    } catch (error) {
      console.error('Passwords import failed:', error);
      result.errors.push('패스워드 가져오기 중 오류가 발생했습니다.');
    }

    return result;
  },

  /**
   * 신용카드 데이터 가져오기
   */
  async importCreditCards(userId, creditCards, options, batch, batchCount) {
    const result = { imported: 0, skipped: 0, errors: [] };
    
    try {
      // 기존 신용카드 목록 (중복 검사용)
      let existingCards = [];
      if (options.skipDuplicates) {
        const cardsRef = collection(db, 'users', userId, 'creditCards');
        const snapshot = await getDocs(cardsRef);
        existingCards = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      for (const cardData of creditCards) {
        try {
          // 중복 검사
          if (options.skipDuplicates && this.isDuplicateCreditCard(cardData, existingCards)) {
            result.skipped++;
            continue;
          }

          if (batchCount >= 450) {
            await batch.commit();
            batchCount = 0;
          }

          // 데이터 암호화
          const encryptedData = {
            cardholderName: cardData.cardholderName ? await encrypt(cardData.cardholderName, userId) : null,
            bankName: cardData.bankName || '',
            cardType: cardData.cardType || '',
            cardNumber: cardData.cardNumber && cardData.cardNumber !== '****' ? 
              await encrypt(cardData.cardNumber, userId) : null,
            expiryDate: cardData.expiryDate && cardData.expiryDate !== '****' ? 
              await encrypt(cardData.expiryDate, userId) : null,
            cvv: cardData.cvv && cardData.cvv !== '***' ? 
              await encrypt(cardData.cvv, userId) : null,
            pin: cardData.pin && cardData.pin !== '****' ? 
              await encrypt(cardData.pin, userId) : null,
            notes: cardData.notes ? await encrypt(cardData.notes, userId) : null,
            createdAt: options.preserveTimestamps && cardData.createdAt ? 
              cardData.createdAt : serverTimestamp(),
            updatedAt: options.preserveTimestamps && cardData.updatedAt ? 
              cardData.updatedAt : serverTimestamp()
          };

          const docRef = doc(collection(db, 'users', userId, 'creditCards'));
          batch.set(docRef, encryptedData);
          result.imported++;
          batchCount++;
        } catch (error) {
          console.error('Credit card import error:', error);
          result.errors.push(`신용카드 "${cardData.cardholderName}" 가져오기 실패`);
        }
      }
    } catch (error) {
      console.error('Credit cards import failed:', error);
      result.errors.push('신용카드 가져오기 중 오류가 발생했습니다.');
    }

    return result;
  },

  /**
   * API 키 데이터 가져오기
   */
  async importApiKeys(userId, apiKeys, options, batch, batchCount) {
    const result = { imported: 0, skipped: 0, errors: [] };
    
    try {
      // 기존 API 키 목록 (중복 검사용)
      let existingApiKeys = [];
      if (options.skipDuplicates) {
        const apiKeysRef = collection(db, 'users', userId, 'apiKeys');
        const snapshot = await getDocs(apiKeysRef);
        existingApiKeys = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      for (const apiKeyData of apiKeys) {
        try {
          // 중복 검사
          if (options.skipDuplicates && this.isDuplicateApiKey(apiKeyData, existingApiKeys)) {
            result.skipped++;
            continue;
          }

          if (batchCount >= 450) {
            await batch.commit();
            batchCount = 0;
          }

          // 데이터 암호화
          const encryptedData = {
            serviceName: apiKeyData.serviceName || '',
            keyName: apiKeyData.keyName || '',
            apiKey: apiKeyData.apiKey && apiKeyData.apiKey !== '****' ? 
              await encrypt(apiKeyData.apiKey, userId) : null,
            secretKey: apiKeyData.secretKey && apiKeyData.secretKey !== '****' ? 
              await encrypt(apiKeyData.secretKey, userId) : null,
            description: apiKeyData.description || '',
            url: apiKeyData.url || '',
            permissions: apiKeyData.permissions || [],
            notes: apiKeyData.notes ? await encrypt(apiKeyData.notes, userId) : null,
            expiresAt: apiKeyData.expiresAt || null,
            createdAt: options.preserveTimestamps && apiKeyData.createdAt ? 
              apiKeyData.createdAt : serverTimestamp(),
            updatedAt: options.preserveTimestamps && apiKeyData.updatedAt ? 
              apiKeyData.updatedAt : serverTimestamp()
          };

          const docRef = doc(collection(db, 'users', userId, 'apiKeys'));
          batch.set(docRef, encryptedData);
          result.imported++;
          batchCount++;
        } catch (error) {
          console.error('API key import error:', error);
          result.errors.push(`API 키 "${apiKeyData.serviceName}" 가져오기 실패`);
        }
      }
    } catch (error) {
      console.error('API keys import failed:', error);
      result.errors.push('API 키 가져오기 중 오류가 발생했습니다.');
    }

    return result;
  },

  /**
   * 보안 메모 데이터 가져오기 (첨부파일 포함)
   */
  async importSecureNotes(userId, secureNotes, options) {
    const result = { imported: 0, skipped: 0, attachments: 0, errors: [] };
    
    try {
      // 기존 보안 메모 목록 (중복 검사용)
      let existingNotes = [];
      if (options.skipDuplicates) {
        const notesRef = collection(db, 'users', userId, 'secureNotes');
        const snapshot = await getDocs(notesRef);
        existingNotes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      for (const noteData of secureNotes) {
        try {
          // 중복 검사
          if (options.skipDuplicates && this.isDuplicateSecureNote(noteData, existingNotes)) {
            result.skipped++;
            continue;
          }

          // 첨부파일 처리
          let attachments = [];
          if (noteData.attachments && Array.isArray(noteData.attachments)) {
            for (const attachment of noteData.attachments) {
              try {
                if (attachment.data) {
                  // Base64 데이터를 Firebase Storage에 업로드
                  const uploadResult = await this.uploadAttachmentFromBase64(
                    userId, 
                    attachment.data, 
                    attachment.name, 
                    attachment.type
                  );
                  attachments.push(uploadResult);
                  result.attachments++;
                } else {
                  // 첨부파일 정보만 있는 경우
                  attachments.push({
                    name: attachment.name,
                    size: attachment.size || 0,
                    type: attachment.type || 'application/octet-stream',
                    url: null
                  });
                }
              } catch (attachError) {
                console.error('Attachment import error:', attachError);
                result.errors.push(`첨부파일 "${attachment.name}" 업로드 실패`);
              }
            }
          }

          // 데이터 암호화
          const encryptedData = {
            title: noteData.title ? await encrypt(noteData.title, userId) : null,
            content: noteData.content ? await encrypt(noteData.content, userId) : null,
            category: noteData.category || 'general',
            tags: noteData.tags || [],
            pin: noteData.pin || '',
            attachments: attachments,
            createdAt: options.preserveTimestamps && noteData.createdAt ? 
              noteData.createdAt : serverTimestamp(),
            updatedAt: options.preserveTimestamps && noteData.updatedAt ? 
              noteData.updatedAt : serverTimestamp()
          };

          await addDoc(collection(db, 'users', userId, 'secureNotes'), encryptedData);
          result.imported++;
        } catch (error) {
          console.error('Secure note import error:', error);
          result.errors.push(`보안 메모 "${noteData.title}" 가져오기 실패`);
        }
      }
    } catch (error) {
      console.error('Secure notes import failed:', error);
      result.errors.push('보안 메모 가져오기 중 오류가 발생했습니다.');
    }

    return result;
  },

  /**
   * Base64 첨부파일을 Firebase Storage에 업로드
   */
  async uploadAttachmentFromBase64(userId, base64Data, fileName, fileType) {
    try {
      // Base64를 Blob으로 변환
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileType });

      // Firebase Storage에 업로드
      const timestamp = Date.now();
      const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `secure-notes/${userId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);

      const metadata = {
        contentType: fileType,
        customMetadata: { originalName: fileName }
      };

      await uploadBytes(storageRef, blob, metadata);
      const downloadURL = await getDownloadURL(storageRef);

      return {
        name: fileName,
        size: blob.size,
        type: fileType,
        url: downloadURL,
        path: path
      };
    } catch (error) {
      console.error('Attachment upload failed:', error);
      throw new Error(`첨부파일 "${fileName}" 업로드 실패`);
    }
  },

  /**
   * 기존 사용자 데이터 삭제 (대체 모드)
   */
  async clearUserData(userId) {
    const batch = writeBatch(db);
    let batchCount = 0;

    const collections = ['passwords', 'creditCards', 'apiKeys', 'secureNotes'];
    
    for (const collectionName of collections) {
      const collectionRef = collection(db, 'users', userId, collectionName);
      const snapshot = await getDocs(collectionRef);
      
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        batchCount++;
        
        if (batchCount >= 450) {
          batch.commit();
          batchCount = 0;
        }
      });
    }

    if (batchCount > 0) {
      await batch.commit();
    }
  },

  /**
   * 중복 검사 함수들
   */
  isDuplicatePassword(newPassword, existingPasswords) {
    return existingPasswords.some(existing => 
      existing.site === newPassword.site && 
      existing.username === newPassword.username
    );
  },

  isDuplicateCreditCard(newCard, existingCards) {
    return existingCards.some(existing => 
      existing.bankName === newCard.bankName && 
      existing.cardholderName === newCard.cardholderName
    );
  },

  isDuplicateApiKey(newApiKey, existingApiKeys) {
    return existingApiKeys.some(existing => 
      existing.serviceName === newApiKey.serviceName && 
      existing.keyName === newApiKey.keyName
    );
  },

  isDuplicateSecureNote(newNote, existingNotes) {
    return existingNotes.some(existing => 
      existing.title === newNote.title
    );
  }
};